import { z } from "zod";

export const appointmentStatusSchema = z.enum([
	"booked",
	"confirmed",
	"arrived",
	"in_progress",
	// Doctor ordered labs/imaging mid-visit; patient waits outside while the
	// doctor sees the next patient. Recalled (start) once results land.
	"awaiting_results",
	"completed",
	"no_show",
	"cancelled",
	"rescheduled",
]);
export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>;

export const appointmentSourceSchema = z.enum([
	"phone",
	"whatsapp",
	"instagram",
	"walk_in",
	"web",
	"voice_command",
	"staff_manual",
	"recurring",
]);
export type AppointmentSource = z.infer<typeof appointmentSourceSchema>;

export const appointmentPrioritySchema = z.enum([
	"low",
	"normal",
	"high",
	"vip",
	"emergency",
]);
export type AppointmentPriority = z.infer<typeof appointmentPrioritySchema>;

/** Pre-visit intake — reception/patient-entered context, all optional. */
export const preVisitIntakeSchema = z.object({
	chief_complaint: z.string().max(500).optional(),
	symptom_duration: z.string().max(200).optional(),
	current_meds: z.string().max(1000).optional(),
	history_notes: z.string().max(2000).optional(),
	recorded_by: z.string().max(40).optional(),
});
export type PreVisitIntake = z.infer<typeof preVisitIntakeSchema>;

export const appointmentSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	doctor_id: z.string().uuid().nullable(),
	service_id: z.string().uuid().nullable(),
	scheduled_at: z.string().datetime(),
	duration_minutes: z.number().int().positive(),
	buffer_minutes: z.number().int().nonnegative(),
	status: appointmentStatusSchema,
	source: appointmentSourceSchema,
	priority: appointmentPrioritySchema,
	title: z.string().nullable(),
	notes: z.string().nullable(),
	pre_visit_intake: preVisitIntakeSchema.nullable(),
	is_video: z.boolean(),
	/** Server-computed telemedicine room URL (null for non-video visits). */
	video_room_url: z.string().nullable(),
	cancellation_reason: z.string().nullable(),
	reminders_sent: z.record(z.string(), z.string()),
	arrived_at: z.string().datetime().nullable(),
	started_at: z.string().datetime().nullable(),
	/** Set when results turned final while status was awaiting_results — the
	 *  "🔔 recall the patient" signal for reception/doctor UIs. */
	results_ready_at: z.string().datetime().nullable(),
	completed_at: z.string().datetime().nullable(),
	cancelled_at: z.string().datetime().nullable(),
	/** Correlates the members of a recurring series (null = standalone). */
	recurrence_group_id: z.string().uuid().nullable(),
	created_by: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type Appointment = z.infer<typeof appointmentSchema>;

// The telemedicine room URL is computed server-side (HMAC over the appointment
// id with the server secret) and delivered as appointment.video_room_url — a
// bare appointment UUID must not be enough to join a consultation. See
// apps/server/src/lib/video.ts.

// ===== Input schemas =====

export const createAppointmentInputSchema = z.object({
	patient_id: z.string().uuid(),
	doctor_id: z.string().uuid().optional(),
	service_id: z.string().uuid().optional(),
	scheduled_at: z.string().datetime(),
	duration_minutes: z.number().int().positive().max(720).default(30),
	buffer_minutes: z.number().int().nonnegative().max(60).default(0),
	source: appointmentSourceSchema.default("staff_manual"),
	priority: appointmentPrioritySchema.default("normal"),
	title: z.string().max(200).optional(),
	notes: z.string().max(2000).optional(),
	/**
	 * Optional pre-visit intake captured AT booking (e.g. the patient's stated
	 * reason from public self-booking) so the doctor's intake card shows it
	 * without a separate entry step.
	 */
	pre_visit_intake: preVisitIntakeSchema.optional(),
	is_video: z.boolean().default(false),
	// Staff-only escape hatch for a walk-in / emergency: skip the working-hours
	// + past-time guards so an actually-seen patient can be booked off-schedule
	// or back-dated. Patient/public self-booking never sets this.
	allow_outside_hours: z.boolean().default(false),
	/** Set by the recurring-series creator to correlate the occurrences. */
	recurrence_group_id: z.string().uuid().optional(),
	// When set, this booking REPLACES an existing appointment: the original is
	// moved to status 'rescheduled' atomically and linked via rescheduled_from,
	// so the schedule never carries a phantom duplicate.
	reschedule_of: z.string().uuid().optional(),
});
export type CreateAppointmentInput = z.infer<
	typeof createAppointmentInputSchema
>;

/**
 * One-call atomic walk-in: register the visit for "now" and check the patient
 * in — and, in "start" mode, begin the visit — inside a single DB transaction.
 * A rejection (doctor already mid-visit) rolls everything back, so it can
 * never leave an orphan appointment behind (unlike create→arrive→start as
 * three separate requests).
 */
export const walkInAppointmentInputSchema = z.object({
	patient_id: z.string().uuid(),
	doctor_id: z.string().uuid().optional(),
	service_id: z.string().uuid().optional(),
	duration_minutes: z.number().int().positive().max(720).default(30),
	priority: appointmentPrioritySchema.default("high"),
	title: z.string().max(200).optional(),
	/**
	 * "start" → arrived + in_progress now (doctor free).
	 * "queue" → arrived only: reception keeps taking walk-ins while the
	 *           doctor is busy; the visit starts later from the queue.
	 */
	mode: z.enum(["start", "queue"]).default("start"),
});
export type WalkInAppointmentInput = z.infer<
	typeof walkInAppointmentInputSchema
>;

/** Set/replace the pre-visit intake on an appointment. */
export const setPreVisitIntakeInputSchema = z.object({
	intake: preVisitIntakeSchema.nullable(),
});
export type SetPreVisitIntakeInput = z.infer<
	typeof setPreVisitIntakeInputSchema
>;

export const updateAppointmentInputSchema = z.object({
	doctor_id: z.string().uuid().nullable().optional(),
	service_id: z.string().uuid().nullable().optional(),
	scheduled_at: z.string().datetime().optional(),
	duration_minutes: z.number().int().positive().max(720).optional(),
	buffer_minutes: z.number().int().nonnegative().max(60).optional(),
	priority: appointmentPrioritySchema.optional(),
	title: z.string().max(200).nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
	is_video: z.boolean().optional(),
	// Same walk-in / emergency escape hatch as create: skip the working-hours
	// guard when rescheduling an off-schedule visit.
	allow_outside_hours: z.boolean().optional(),
});
export type UpdateAppointmentInput = z.infer<
	typeof updateAppointmentInputSchema
>;

export const appointmentStatusActionSchema = z.enum([
	"confirm",
	"arrive",
	"start",
	// Park an in_progress visit while labs/imaging run — frees the doctor's
	// single-in-progress slot without losing the patient from the day.
	"await_results",
	"complete",
	"no_show",
	"cancel",
	"reopen",
]);
export type AppointmentStatusAction = z.infer<
	typeof appointmentStatusActionSchema
>;

export const transitionAppointmentInputSchema = z.object({
	action: appointmentStatusActionSchema,
	reason: z.string().max(500).optional(),
});
export type TransitionAppointmentInput = z.infer<
	typeof transitionAppointmentInputSchema
>;

export const appointmentsListQuerySchema = z.object({
	patient_id: z.string().uuid().optional(),
	doctor_id: z.string().uuid().optional(),
	status: appointmentStatusSchema.optional(),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
	page: z.coerce.number().int().positive().default(1),
	per_page: z.coerce.number().int().positive().max(200).default(50),
	order: z.enum(["asc", "desc"]).default("asc"),
});
export type AppointmentsListQuery = z.infer<typeof appointmentsListQuerySchema>;

// Returned alongside appointment when joined for UI display
// ===== Recurring series =====
// One booking form submit → N linked appointments (weekly physio, monthly
// ortho adjustment…). Server creates each occurrence through the SAME
// createAppointment guards (working hours, conflicts); occurrences that fail
// are reported back as skipped, never silently dropped.

export const recurrenceIntervalSchema = z.enum([
	"weekly",
	"biweekly",
	"monthly",
]);
export type RecurrenceInterval = z.infer<typeof recurrenceIntervalSchema>;

export const createRecurringInputSchema = z.object({
	base: createAppointmentInputSchema.omit({ reschedule_of: true }),
	interval: recurrenceIntervalSchema,
	/** Total occurrences INCLUDING the first one. */
	count: z.number().int().min(2).max(12),
});
export type CreateRecurringInput = z.infer<typeof createRecurringInputSchema>;

export interface CreateRecurringResult {
	recurrence_group_id: string;
	created: Appointment[];
	skipped: Array<{ scheduled_at: string; reason: string }>;
}

export interface AppointmentWithPatient extends Appointment {
	patient_name: string | null;
	patient_phone: string | null;
	patient_mrn: string | null;
	/** Prior documented visits for this patient in the clinic — >0 means a
	 *  returning patient (drives the "returning" badge on the check-in list). */
	patient_visit_count?: number;
}
